import React, { useState, useEffect } from "react";
import { MessageDisplay } from "../common/messageDisplay";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  TableSortLabel,
  Checkbox,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { MuiComboBox } from "./muiComboBox";
import useHttpRequests from "../../hooks/useHttpRequests";
import "../uiControls/commonStyles.css";
import * as utils from "../common/utils";

const thBackgoundColor = "#3a8ddd";
const thFontColor = "white";
const thFontWeight = "bold";

export const TableWithStickyHeaderToAddNew = (props) => {
  const {
    title,
    pagging,
    columns,
    url,
    sourceObj,
    list,
    selectedRows,
    setSelectedRows,
  } = props;
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [searchTimer, setSearchTimer] = useState(null);

  const paperStyles = !pagging
    ? { width: "100%", height: "calc(100vh - 220px)" }
    : { width: "100%", overflow: "auto" };

  const { isLoading, sendRequest: fetchData } = useHttpRequests();

  const dataFetch = () => {
    const handleFetchResponse = (resObj) => {
      if (!resObj || !resObj.isSuccess || resObj.errorResponse) {
        console.log("User fetch response returns error");
        return;
      }
      if (pagging) {
        setDataCount(
          resObj["recordsTotal"]
            ? resObj["recordsTotal"]
            : resObj[sourceObj]?.length
        );
      }
      setData(resObj[sourceObj]);
    };

    const generateQuery = () => {
      let params =
        "pageSize=" +
        rowsPerPage +
        "&pageIndex=" +
        (page + 1) +
        "&isAdd=true&isActive=true&orderByColumn=" +
        orderBy +
        "&orderByDirection=" +
        order;

      if (searchQuery !== "") {
        params += "&searchText=" + searchQuery;
      }
      return url(params);
    };

    fetchData(
      {
        url: generateQuery(),
        method: "GET",
        header: true,
      },
      handleFetchResponse
    );
  };

  useEffect(() => {
    dataFetch();
  }, [page, order, orderBy, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchQueryChange = (event) => {
    const { value } = event.target;
    setSearchQuery(value);
  };

  const handleSearchQueryKeyPress = (event) => {
    if (event.key === "Enter") {
      setPage(0);
      dataFetch();
    }
  };

  useEffect(() => {
    clearTimeout(searchTimer);
    const timerId = setTimeout(() => {
      setPage(0);
      dataFetch();
    }, 1000);
    setSearchTimer(timerId);
  }, [searchQuery]);

  const handleSort = (fieldName) => {
    const isAsc = orderBy === fieldName && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(fieldName);
    setPage(0);
  };

  const handleInputChange = (event, id) => {
    const { name, value } = event.target;
    setData((prevData) =>
      prevData.map((row) =>
        row[columns[0]["keyField"]] === id ? { ...row, [name]: value } : row
      )
    );
    setSelectedRows((prevData) =>
      prevData.map((row) =>
        row[columns[0]["keyField"]] === id ? { ...row, [name]: value } : row
      )
    );
  };

  const handleSelectChange = (field, id, value) => {
    setData((prevData) =>
      prevData.map((row) =>
        row[columns[0]["keyField"]] === id ? { ...row, [field]: value } : row
      )
    );
    setSelectedRows((prevData) =>
      prevData.map((row) =>
        row[columns[0]["keyField"]] === id ? { ...row, [field]: value } : row
      )
    );
  };

  const handleRowSelect = (row) => {
    if (isRowSelected(row[columns[0]["keyField"]])) {
      setSelectedRows(
        selectedRows.filter(
          (selectedRow) =>
            selectedRow[columns[0]["keyField"]] !== row[columns[0]["keyField"]]
        )
      );
    } else {
      setSelectedRows([...selectedRows, row]);
    }
  };

  const isRowSelected = (value) => {
    return (
      selectedRows.filter((item) => item[columns[0]["keyField"]] === value)
        ?.length > 0
    );
  };

  return (
    <div>
      <Backdrop className="backDrop" open={isLoading}>
        <CircularProgress color="inherit" />
        <p>Please wait...</p>
      </Backdrop>
      <MessageDisplay />
      <div style={{ display: "list-item" }}>
        <label className="title">{title}</label>
        <div style={{ marginBottom: 5, float: "right" }}>
          <TextField
            size="small"
            placeholder="Search"
            value={searchQuery}
            onKeyUp={handleSearchQueryKeyPress}
            onChange={handleSearchQueryChange}
          />
        </div>
      </div>

      <Paper sx={paperStyles}>
        <TableContainer sx={{ height: "100%" }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns?.map((column, index) => (
                  <TableCell
                    key={column.label + "_" + index}
                    align={column["align"]}
                    style={{
                      backgroundColor: thBackgoundColor,
                      color: thFontColor,
                      fontWeight: thFontWeight,
                      padding: "8px 0px 8px 8px",
                      minWidth: column["minWidth"]
                        ? column["minWidth"]
                        : column["maxWidth"]
                        ? column["maxWidth"]
                        : 150,
                    }}
                  >
                    {column.label !== "" &&
                      ((column["fieldType"] !== "dropdown" && (
                        <TableSortLabel
                          active={orderBy === column["dataField"]}
                          direction={order}
                          onClick={() => handleSort(column["dataField"])}
                        >
                          {column.label}
                        </TableSortLabel>
                      )) ||
                        (column["fieldType"] === "dropdown" && (
                          <>&nbsp;{column.label}</>
                        )))}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((row, idx) => {
                return (
                  <TableRow
                    sx={{ lineHeight: "10px", padding: "0px" }}
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={"row_" + idx}
                  >
                    {columns.map((col, colIdx) => {
                      const value = row[col["dataField"]];
                      return (
                        <TableCell
                          sx={{
                            lineHeight: "10x",
                            padding: "0px 0px 0px 8px",
                          }}
                          key={"cols_" + colIdx + "_" + row[col["keyField"]]}
                          align={col["align"]}
                        >
                          {col["fieldType"] === "number" &&
                            col["label"] === "" && (
                              <Checkbox
                                sx={{ p: 0 }}
                                checked={isRowSelected(row[col["keyField"]])}
                                onChange={() => handleRowSelect(row)}
                              />
                            )}
                          {col["fieldType"] === "label" && <>{value}</>}
                          {col["fieldType"] === "email" && (
                            <TextField
                              type={col["fieldType"]}
                              size="small"
                              id={
                                col["fieldType"] +
                                "_" +
                                colIdx +
                                "_" +
                                row[col["keyField"]]
                              }
                              name={col["dataField"]}
                              onChange={(event) =>
                                handleInputChange(event, row[col["keyField"]])
                              }
                              value={value || ""}
                              maxLength={50}
                              error={
                                row[col["dataField"]] !== null &&
                                row[col["dataField"]] !== ""
                                  ? !utils.validateEmail(row[col["dataField"]])
                                  : false
                              }
                            />
                          )}
                          {typeof value === "boolean" &&
                            (row["active"] ? (
                              <ToggleOnIcon
                                sx={{ color: "green", fontSize: "2rem" }}
                              />
                            ) : (
                              <ToggleOffIcon
                                sx={{ color: "grey", fontSize: "2rem" }}
                              />
                            ))}
                          {col["fieldType"] === "dropdown" && (
                            <MuiComboBox
                              name={col["dataField"]}
                              id={row[col["keyField"]]}
                              list={list[col["listSource"]]}
                              isError={
                                isRowSelected(row[col["keyField"]]) &&
                                row[col["dataField"]] === 0
                              }
                              onChange={handleSelectChange}
                            />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {pagging && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 15, 20, 25]}
            component="div"
            count={dataCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </div>
  );
};
